import type { ReactNode } from "react";
import { AlertTriangle } from "./Icon";

/**
 * Loading / empty / error surfaces — composes `@norkron/web-states` +
 * `@norkron/web-skeleton`. Inline, never a spinner-modal (design principle 2:
 * stay in the reader). Empty states TEACH the next action (principle 6).
 */

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="state" role="status" aria-live="polite">
      <div className="spinner" aria-hidden />
      {label ? <div className="state__body">{label}</div> : null}
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  body,
  action,
}: {
  icon?: ReactNode;
  title: string;
  body?: string;
  action?: ReactNode;
}) {
  return (
    <div className="state" data-testid="empty-state">
      {icon ? <div className="state__icon">{icon}</div> : null}
      <div className="state__title">{title}</div>
      {body ? <div className="state__body">{body}</div> : null}
      {action ? <div>{action}</div> : null}
    </div>
  );
}

export function ErrorState({
  title = "Something went wrong",
  body,
  action,
}: {
  title?: string;
  body?: string;
  action?: ReactNode;
}) {
  return (
    <div className="state state--error" role="alert" data-testid="error-state">
      <div className="state__icon">
        <AlertTriangle size={30} strokeWidth={1.5} />
      </div>
      <div className="state__title">{title}</div>
      {body ? <div className="state__body">{body}</div> : null}
      {action ? <div>{action}</div> : null}
    </div>
  );
}

export function Skeleton({
  height = 16,
  width = "100%",
  radius,
}: {
  height?: number | string;
  width?: number | string;
  radius?: number;
}) {
  return (
    <div
      className="skeleton"
      aria-hidden
      style={{
        height: typeof height === "number" ? `${height}px` : height,
        width: typeof width === "number" ? `${width}px` : width,
        borderRadius: radius ? `${radius}px` : undefined,
      }}
    />
  );
}

export function SkeletonRows({ rows = 4 }: { rows?: number }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: 16 }}>
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} height={44} />
      ))}
    </div>
  );
}
